import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../api';
import { writePrintDoc } from '../utils/printUtils';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { ArrowLeft, Download, Calendar, IndianRupee, Printer, Award, ExternalLink } from 'lucide-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const LoanDetail = () => {
  const { loanId } = useParams();
  const navigate = useNavigate();
  const [loan, setLoan] = useState(null);
  const [entrepreneur, setEntrepreneur] = useState(null);
  const [collections, setCollections] = useState([]);
  const [emiSchedule, setEmiSchedule] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLoanDetails();
  }, [loanId]);

  const fetchLoanDetails = async () => {
    try {
      const loanResponse = await api.get(`/loans/${loanId}`);
      const loanData = loanResponse.data;

      // Fetch branch name
      if (loanData.branch_id) {
        try {
          const branchResp = await api.get(`/branches/${loanData.branch_id}`);
          loanData.branch_name = branchResp.data.name;
        } catch (err) {
          loanData.branch_name = '';
        }
      }

      setLoan(loanData);

      // Fetch entrepreneur details with CRP-EP info
      if (loanResponse.data.entrepreneur_id) {
        try {
          const entrepreneurResponse = await api.get(`/entrepreneurs/${loanResponse.data.entrepreneur_id}`);
          setEntrepreneur(entrepreneurResponse.data);
        } catch (err) {
          console.log('Could not fetch entrepreneur details');
        }
      }

      const collectionsResponse = await api.get('/loan-collections', {
        params: { loan_id: loanData.loan_id || loanId },
      });
      setCollections(collectionsResponse.data);

      // Generate EMI Schedule
      generateEMISchedule(loanResponse.data);
    } catch (error) {
      toast.error('Failed to fetch loan details');
    } finally {
      setLoading(false);
    }
  };

  const generateEMISchedule = (loanData) => {
    // NRLM SHG Reducing Balance Method
    // Fixed Principal = Loan Amount / Tenure
    // Interest = Outstanding Balance × Monthly Rate (reduces each month)
    const schedule = [];
    const startDate = new Date(loanData.disbursement_date);
    let remainingPrincipal = loanData.loan_amount;
    const monthlyRate = loanData.interest_rate / 100 / 12;
    const fixedPrincipal = loanData.loan_amount / loanData.tenure_months;

    for (let i = 1; i <= loanData.tenure_months; i++) {
      const dueDate = new Date(startDate);
      dueDate.setMonth(startDate.getMonth() + i);

      // Interest on current outstanding balance (reducing)
      const interestAmount = remainingPrincipal * monthlyRate;
      // EMI = Fixed Principal + Interest on Outstanding
      const emiAmount = fixedPrincipal + interestAmount;
      
      schedule.push({
        installment: i,
        dueDate: dueDate.toISOString().split('T')[0],
        openingBalance: remainingPrincipal,
        emi: emiAmount,
        principal: fixedPrincipal,
        interest: interestAmount,
        balance: Math.max(0, remainingPrincipal - fixedPrincipal),
      });
      
      remainingPrincipal -= fixedPrincipal;
    }

    setEmiSchedule(schedule);
  };

  const downloadEMISchedule = () => {
    if (!loan) return;

    const doc = new jsPDF();

    // Header
    doc.setFontSize(20);
    doc.setTextColor(4, 120, 87);
    doc.text('WWAGS MIS Portal', 14, 20);

    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text('Loan EMI Schedule (NRLM SHG Reducing Balance)', 14, 30);

    // Calculate totals
    const fixedPrincipal = loan.loan_amount / loan.tenure_months;
    const totalInterest = emiSchedule.reduce((sum, item) => sum + item.interest, 0);
    
    // Loan Details
    doc.setFontSize(10);
    doc.text(`Loan ID: ${loan.loan_id}`, 14, 40);
    doc.text(`Entrepreneur: ${loan.entrepreneur_name}`, 14, 46);
    doc.text(`Loan Amount: Rs. ${loan.loan_amount.toLocaleString('en-IN')}`, 14, 52);
    doc.text(`Interest Rate: ${loan.interest_rate}% p.a.`, 14, 58);
    doc.text(`Tenure: ${loan.tenure_months} months`, 100, 40);
    doc.text(`Fixed Principal/Month: Rs. ${fixedPrincipal.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`, 100, 46);
    doc.text(`Total Interest: Rs. ${totalInterest.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`, 100, 52);
    doc.text(`Total Payable: Rs. ${(loan.loan_amount + totalInterest).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`, 100, 58);

    // EMI Schedule Table
    const tableData = emiSchedule.map((item) => [
      item.installment,
      new Date(item.dueDate).toLocaleDateString('en-IN'),
      `Rs. ${item.openingBalance?.toFixed(0) || '0'}`,
      `Rs. ${item.principal.toFixed(0)}`,
      `Rs. ${item.interest.toFixed(0)}`,
      `Rs. ${item.emi.toFixed(0)}`,
      `Rs. ${item.balance.toFixed(0)}`,
    ]);

    autoTable(doc, {
      startY: 68,
      head: [['#', 'Due Date', 'Opening Bal', 'Principal', 'Interest', 'EMI', 'Closing Bal']],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: [4, 120, 87],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      styles: {
        fontSize: 9,
        cellPadding: 3,
      },
    });

    doc.save(`EMI_Schedule_${loan.loan_id}.pdf`);
    toast.success('EMI schedule downloaded');
  };

  const generateLoanCertificate = async (mode = 'pdf') => {
    if (!loan || loan.status !== 'closed') return;
    
    const branchName = loan.branch_name || '';
    const entName = entrepreneur?.name || loan.entrepreneur_name || '';
    const entAddress = entrepreneur?.address || '';
    const entDistrict = entrepreneur?.district || '';
    const entState = entrepreneur?.state || 'Manipur';
    const entMobile = entrepreneur?.mobile || '';
    const shgName = entrepreneur?.shg_vlf_name || '';
    const closureDate = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
    const certNo = `WWAGS/LC/${loan.loan_id}/${new Date().getFullYear()}`;
    const totalCollected = loan.total_amount - loan.outstanding_amount;

    // Fetch branch address for certificate
    let branchAddress = '';
    if (loan.branch_id) {
      try {
        const brResp = await api.get(`/branches/${loan.branch_id}`);
        branchAddress = brResp.data.address || '';
      } catch (e) {}
    }

    // BRC header with branch name
    const brcTitle = `BLOCK RESOURCE CENTRE`;
    const brcBranch = branchName ? `${branchName.toUpperCase()}` : 'SVEP';
    const brcAddress = branchAddress ? branchAddress : '';
    const nroPia = 'Implementing NRO/PIA : Wangjing Women and Girls Society (WWAGS), Manipur';

    if (mode === 'print') {
      const htmlContent = `<html><head><title>Loan Closure Certificate - ${loan.loan_id}</title>
        <style>
          @page { margin: 20mm; size: A4 portrait; }
          body { font-family: 'Times New Roman', Georgia, serif; padding: 30px 40px; margin: 0; color: #1a1a1a; }
          .border-frame { border: 3px double #047857; padding: 30px; min-height: 700px; position: relative; }
          .corner { position: absolute; width: 30px; height: 30px; border-color: #047857; }
          .corner-tl { top: 8px; left: 8px; border-top: 2px solid; border-left: 2px solid; }
          .corner-tr { top: 8px; right: 8px; border-top: 2px solid; border-right: 2px solid; }
          .corner-bl { bottom: 8px; left: 8px; border-bottom: 2px solid; border-left: 2px solid; }
          .corner-br { bottom: 8px; right: 8px; border-bottom: 2px solid; border-right: 2px solid; }
          .header { text-align: center; margin-bottom: 25px; }
          .org-name { font-size: 22px; font-weight: bold; color: #047857; letter-spacing: 2px; margin-bottom: 3px; }
          .org-sub { font-size: 13px; color: #555; margin-bottom: 2px; }
          .cert-title { font-size: 20px; font-weight: bold; color: #1a1a1a; margin: 20px 0 5px; text-decoration: underline; text-underline-offset: 6px; letter-spacing: 3px; }
          .cert-no { font-size: 11px; color: #666; margin-bottom: 20px; }
          .body-text { font-size: 14px; line-height: 2; text-align: justify; margin: 15px 0; }
          .body-text b { color: #047857; }
          .details-table { width: 100%; border-collapse: collapse; margin: 15px 0; }
          .details-table td { padding: 6px 10px; font-size: 13px; border-bottom: 1px dashed #ddd; }
          .details-table td:first-child { font-weight: bold; color: #333; width: 40%; }
          .seal-section { display: flex; justify-content: space-between; align-items: flex-end; margin-top: 50px; }
          .seal-box { text-align: center; width: 200px; }
          .seal-line { border-top: 1px solid #333; margin-top: 50px; padding-top: 5px; font-size: 12px; }
          .stamp-area { text-align: center; font-size: 11px; color: #999; border: 1px dashed #ccc; padding: 15px; border-radius: 50%; width: 80px; height: 80px; display: flex; align-items: center; justify-content: center; margin: 0 auto; }
          .footer-note { font-size: 10px; color: #888; text-align: center; margin-top: 30px; font-style: italic; }
          @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        </style></head>
        <body>
          <div class="border-frame">
            <div class="corner corner-tl"></div>
            <div class="corner corner-tr"></div>
            <div class="corner corner-bl"></div>
            <div class="corner corner-br"></div>
            
            <div class="header">
              <div class="org-name">${brcTitle}</div>
              <div class="org-sub" style="font-size:15px; font-weight:bold; color:#333; margin-bottom:2px;">${brcBranch}</div>
              ${brcAddress ? `<div class="org-sub" style="font-size:11px; color:#555;">${brcAddress}</div>` : ''}
              <div class="org-sub" style="font-size:11px; color:#047857; font-style:italic; margin-top:3px;">${nroPia}</div>
              <div class="cert-title">LOAN CLOSURE CERTIFICATE</div>
              <div class="cert-no">Certificate No: ${certNo} &nbsp;&nbsp;|&nbsp;&nbsp; Date: ${closureDate}</div>
            </div>

            <div class="body-text">
              This is to certify that <b>${entName}</b>${shgName ? ', member of <b>' + shgName + '</b>' : ''}${entAddress ? ', residing at <b>' + entAddress + (entDistrict ? ', ' + entDistrict : '') + (entState ? ', ' + entState : '') + '</b>' : ''}, has successfully repaid the loan in full as per the following details:
            </div>

            <table class="details-table">
              <tr><td>Loan ID</td><td>${loan.loan_id}</td></tr>
              <tr><td>Loan Type</td><td>${loan.loan_type}</td></tr>
              <tr><td>Loan Amount</td><td>Rs. ${loan.loan_amount.toLocaleString('en-IN')}</td></tr>
              <tr><td>Interest Rate</td><td>${loan.interest_rate}% per annum</td></tr>
              <tr><td>Tenure</td><td>${loan.tenure_months} months</td></tr>
              <tr><td>Date of Disbursement</td><td>${new Date(loan.disbursement_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</td></tr>
              <tr><td>Total Amount Payable</td><td>Rs. ${loan.total_amount.toLocaleString('en-IN')}</td></tr>
              <tr><td>Total Amount Repaid</td><td>Rs. ${totalCollected.toLocaleString('en-IN')}</td></tr>
              <tr><td>Outstanding Balance</td><td>Rs. ${loan.outstanding_amount.toLocaleString('en-IN')}</td></tr>
              <tr><td>Branch</td><td>${branchName}</td></tr>
              ${entMobile ? '<tr><td>Contact</td><td>' + entMobile + '</td></tr>' : ''}
            </table>

            <div class="body-text">
              It is hereby confirmed that <b>${entName}</b> has no outstanding dues with the Block Resource Centre, ${branchName}. This certificate is issued upon the request of the borrower for their records.
            </div>

            <div class="seal-section">
              <div class="seal-box">
                <div class="seal-line">Borrower's Signature</div>
              </div>
              <div class="stamp-area">Official Seal</div>
              <div class="seal-box">
                <div class="seal-line">Authorized Signatory<br/>BRC, ${branchName || 'WWAGS'}</div>
              </div>
            </div>

            <div class="footer-note">
              This is a computer-generated certificate. Valid without physical signature if verified digitally.
            </div>
          </div>
        </body></html>`;
      
      const pw = window.open('', '_blank');
      if (!pw) {
        const iframe = document.createElement('iframe');
        iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
        document.body.appendChild(iframe);
        writePrintDoc(iframe.contentDocument, htmlContent);
        setTimeout(() => { iframe.contentWindow.print(); setTimeout(() => document.body.removeChild(iframe), 1000); }, 500);
        return;
      }
      writePrintDoc(pw.document, htmlContent);
      setTimeout(() => pw.print(), 500);
      return;
    }

    // PDF mode
    const doc = new jsPDF();
    
    // Border frame
    doc.setDrawColor(4, 120, 87);
    doc.setLineWidth(0.5);
    doc.rect(10, 10, 190, 277);
    doc.setLineWidth(0.3);
    doc.rect(12, 12, 186, 273);

    // Header
    doc.setFontSize(18);
    doc.setTextColor(4, 120, 87);
    doc.setFont(undefined, 'bold');
    doc.text('BLOCK RESOURCE CENTRE', 105, 28, { align: 'center' });
    
    doc.setFontSize(13);
    doc.setTextColor(50, 50, 50);
    doc.text(brcBranch, 105, 36, { align: 'center' });
    
    let pdfY = 42;
    if (brcAddress) {
      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      doc.text(brcAddress, 105, pdfY, { align: 'center' });
      pdfY += 6;
    }
    
    doc.setFontSize(9);
    doc.setTextColor(4, 120, 87);
    doc.setFont(undefined, 'italic');
    doc.text(nroPia, 105, pdfY, { align: 'center' });
    doc.setFont(undefined, 'normal');
    
    // Line separator
    doc.setDrawColor(4, 120, 87);
    doc.setLineWidth(0.5);
    doc.line(30, 45, 180, 45);

    // Certificate Title
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'bold');
    doc.text('LOAN CLOSURE CERTIFICATE', 105, 55, { align: 'center' });
    
    // Certificate number and date
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.setFont(undefined, 'normal');
    doc.text(`Certificate No: ${certNo}`, 30, 62);
    doc.text(`Date: ${closureDate}`, 180, 62, { align: 'right' });

    // Body text
    doc.setFontSize(11);
    doc.setTextColor(30, 30, 30);
    let bodyText = `This is to certify that ${entName}`;
    if (shgName) bodyText += `, member of ${shgName}`;
    if (entAddress) bodyText += `, residing at ${entAddress}${entDistrict ? ', ' + entDistrict : ''}${entState ? ', ' + entState : ''}`;
    bodyText += ', has successfully repaid the loan in full as per the following details:';
    
    const splitBody = doc.splitTextToSize(bodyText, 160);
    doc.text(splitBody, 25, 75);
    
    let yPos = 75 + splitBody.length * 6 + 5;

    // Loan Details Table
    autoTable(doc, {
      startY: yPos,
      margin: { left: 25, right: 25 },
      head: [],
      body: [
        ['Loan ID', loan.loan_id],
        ['Loan Type', loan.loan_type],
        ['Loan Amount', `Rs. ${loan.loan_amount.toLocaleString('en-IN')}`],
        ['Interest Rate', `${loan.interest_rate}% per annum`],
        ['Tenure', `${loan.tenure_months} months`],
        ['Date of Disbursement', new Date(loan.disbursement_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })],
        ['Total Amount Payable', `Rs. ${loan.total_amount.toLocaleString('en-IN')}`],
        ['Total Amount Repaid', `Rs. ${totalCollected.toLocaleString('en-IN')}`],
        ['Outstanding Balance', `Rs. ${loan.outstanding_amount.toLocaleString('en-IN')}`],
        ['Branch', branchName],
      ],
      theme: 'plain',
      styles: { fontSize: 10, cellPadding: 3 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 60 },
        1: { cellWidth: 100 },
      },
      alternateRowStyles: { fillColor: [245, 250, 245] },
    });

    yPos = doc.lastAutoTable.finalY + 10;

    // No dues statement
    const noDuesText = `It is hereby confirmed that ${entName} has no outstanding dues with the Block Resource Centre, ${branchName}. This certificate is issued upon the request of the borrower for their records.`;
    const splitNoDues = doc.splitTextToSize(noDuesText, 160);
    doc.text(splitNoDues, 25, yPos);

    yPos = yPos + splitNoDues.length * 6 + 30;

    // Signature lines
    doc.setFontSize(10);
    doc.setTextColor(30, 30, 30);
    doc.line(25, yPos, 75, yPos);
    doc.text("Borrower's Signature", 30, yPos + 5);
    
    doc.line(135, yPos, 185, yPos);
    doc.text('Authorized Signatory', 140, yPos + 5);
    doc.text(`BRC, ${branchName || 'WWAGS'}`, 143, yPos + 10);

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text('This is a computer-generated certificate. Valid without physical signature if verified digitally.', 105, 280, { align: 'center' });

    doc.save(`Loan_Closure_Certificate_${loan.loan_id}.pdf`);
    toast.success('Loan Closure Certificate downloaded');
  };


  const downloadLedger = () => {
    if (!loan) return;

    const doc = new jsPDF('landscape');

    // Header
    doc.setFontSize(20);
    doc.setTextColor(4, 120, 87);
    doc.text('WWAGS MIS Portal', 14, 20);

    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text('Loan Collection Ledger', 14, 30);

    // Entrepreneur Details Section
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('Entrepreneur Details', 14, 42);
    doc.setFont(undefined, 'normal');
    doc.setFontSize(10);
    
    let yPos = 50;
    doc.text(`Name: ${loan.entrepreneur_name || 'N/A'}`, 14, yPos);
    doc.text(`Loan ID: ${loan.loan_id}`, 140, yPos);
    yPos += 6;
    
    if (entrepreneur) {
      doc.text(`Mobile: ${entrepreneur.mobile || 'N/A'}`, 14, yPos);
      doc.text(`Address: ${entrepreneur.address || 'N/A'}`, 140, yPos);
      yPos += 6;
      doc.text(`District: ${entrepreneur.district || 'N/A'}`, 14, yPos);
      doc.text(`State: ${entrepreneur.state || 'N/A'}`, 140, yPos);
      yPos += 6;
      doc.text(`SHG/VLF: ${entrepreneur.shg_vlf_name || 'N/A'}`, 14, yPos);
      doc.text(`SHG Member: ${entrepreneur.shg_member_name || 'N/A'} (${entrepreneur.shg_member_relation || 'N/A'})`, 140, yPos);
      yPos += 6;
      
      // CRP-EP Details
      if (entrepreneur.crp_ep_name) {
        doc.setFont(undefined, 'bold');
        doc.text(`Promoted by CRP-EP: ${entrepreneur.crp_ep_name}`, 14, yPos);
        doc.setFont(undefined, 'normal');
        yPos += 6;
      }
    }
    
    // Loan Details
    yPos += 4;
    doc.setFont(undefined, 'bold');
    doc.text('Loan Details', 14, yPos);
    doc.setFont(undefined, 'normal');
    yPos += 6;
    doc.text(`Loan Amount: Rs. ${loan.loan_amount?.toLocaleString('en-IN') || 0}`, 14, yPos);
    doc.text(`Interest Rate: ${loan.interest_rate || 0}% p.a.`, 100, yPos);
    doc.text(`Tenure: ${loan.tenure_months || 0} months`, 180, yPos);
    yPos += 6;
    doc.text(`Disbursement Date: ${loan.disbursement_date ? new Date(loan.disbursement_date).toLocaleDateString('en-IN') : 'N/A'}`, 14, yPos);
    doc.text(`Monthly EMI: Rs. ${loan.emi_amount?.toLocaleString('en-IN') || 0}`, 100, yPos);
    doc.text(`Total Payable: Rs. ${loan.total_amount?.toLocaleString('en-IN') || 0}`, 180, yPos);
    yPos += 6;
    doc.text(`Outstanding: Rs. ${loan.outstanding_amount?.toLocaleString('en-IN') || 0}`, 14, yPos);
    doc.text(`Status: ${loan.status || 'N/A'}`, 100, yPos);

    // Collections Table with detailed breakdown
    yPos += 12;
    doc.setFont(undefined, 'bold');
    doc.setFontSize(12);
    doc.text('Collection Details', 14, yPos);
    doc.setFont(undefined, 'normal');
    
    const tableData = collections.map((col, index) => [
      index + 1,
      new Date(col.collection_date).toLocaleDateString('en-IN'),
      `Rs. ${(col.principal_amount || 0).toLocaleString('en-IN')}`,
      `Rs. ${(col.interest_amount || 0).toLocaleString('en-IN')}`,
      `Rs. ${(col.penalty_amount || 0).toLocaleString('en-IN')}`,
      `Rs. ${col.amount.toLocaleString('en-IN')}`,
      col.receipt_number || '-',
      col.payment_mode || '-',
    ]);
    
    // Calculate totals
    const totals = collections.reduce((acc, col) => ({
      principal: acc.principal + (col.principal_amount || 0),
      interest: acc.interest + (col.interest_amount || 0),
      penalty: acc.penalty + (col.penalty_amount || 0),
      total: acc.total + (col.amount || 0),
    }), { principal: 0, interest: 0, penalty: 0, total: 0 });
    
    // Add totals row
    if (collections.length > 0) {
      tableData.push([
        '', 'TOTAL',
        `Rs. ${totals.principal.toLocaleString('en-IN')}`,
        `Rs. ${totals.interest.toLocaleString('en-IN')}`,
        `Rs. ${totals.penalty.toLocaleString('en-IN')}`,
        `Rs. ${totals.total.toLocaleString('en-IN')}`,
        '', ''
      ]);
    }

    autoTable(doc, {
      startY: yPos + 5,
      head: [['Sl.No', 'Date', 'Principal', 'Interest', 'Penalty', 'Total Amount', 'Receipt No.', 'Payment Mode']],
      body: tableData.length > 0 ? tableData : [['', 'No collections recorded', '', '', '', '', '', '']],
      theme: 'grid',
      headStyles: {
        fillColor: [4, 120, 87],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9,
      },
      styles: {
        fontSize: 9,
        cellPadding: 2,
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 15 },
        1: { halign: 'center', cellWidth: 25 },
        2: { halign: 'right', cellWidth: 35 },
        3: { halign: 'right', cellWidth: 35 },
        4: { halign: 'right', cellWidth: 30 },
        5: { halign: 'right', cellWidth: 40 },
        6: { halign: 'center', cellWidth: 35 },
        7: { halign: 'center', cellWidth: 30 },
      },
      didParseCell: function(data) {
        // Bold the totals row
        if (data.row.index === tableData.length - 1 && collections.length > 0) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [240, 253, 244];
        }
      }
    });

    doc.save(`Loan_Ledger_${loan.loan_id}.pdf`);
    toast.success('Loan ledger downloaded');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  if (!loan) {
    return (
      <div className="p-6 md:p-8 lg:p-10">
        <div className="text-center py-12">
          <p className="text-slate-500">Loan not found</p>
          <Button onClick={() => navigate('/loans')} className="mt-4">
            Back to Loans
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 lg:p-10" data-testid="loan-detail-page">
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/loans')}
          className="mb-4 print:hidden"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Loans
        </Button>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Loan Details</h1>
            <p className="text-lg text-slate-600">{loan.loan_id}</p>
          </div>

          <div className="flex gap-2 print:hidden">
            <Button
              variant="outline"
              onClick={() => window.print()}
              data-testid="print-loan-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
            <Button
              onClick={downloadEMISchedule}
              data-testid="download-emi-button"
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              EMI Schedule
            </Button>
            <Button
              onClick={downloadLedger}
              variant="outline"
              data-testid="download-ledger-button"
            >
              <Download className="w-4 h-4 mr-2" />
              Ledger
            </Button>
            {loan.status === 'closed' && (
              <>
                <Button
                  onClick={() => generateLoanCertificate('pdf')}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="download-certificate-button"
                >
                  <Award className="w-4 h-4 mr-2" />
                  Certificate PDF
                </Button>
                <Button
                  onClick={() => generateLoanCertificate('print')}
                  variant="outline"
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  data-testid="print-certificate-button"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print Certificate
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Loan Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <p className="text-sm text-slate-600 mb-1">Entrepreneur</p>
          <p className="text-xl font-bold text-slate-900">{loan.entrepreneur_name}</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <p className="text-sm text-slate-600 mb-1">Loan Amount</p>
          <p className="text-xl font-bold text-slate-900">₹{loan.loan_amount.toLocaleString('en-IN')}</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <p className="text-sm text-slate-600 mb-1">Monthly EMI</p>
          <p className="text-xl font-bold text-emerald-700">₹{loan.emi_amount.toLocaleString('en-IN')}</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <p className="text-sm text-slate-600 mb-1">Outstanding</p>
          <p className="text-xl font-bold text-red-600">₹{loan.outstanding_amount.toLocaleString('en-IN')}</p>
        </div>
      </div>

      {/* Entrepreneur Details with Photo and CRP-EP */}
      {entrepreneur && (
        <div className="mb-6 bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Entrepreneur Details</h3>
          <div className="flex flex-col md:flex-row gap-6">
            {/* Passport Photo */}
            <div className="flex-shrink-0">
              {entrepreneur.photo_url ? (
                <img
                  src={entrepreneur.photo_url.startsWith('data:') ? entrepreneur.photo_url : `${process.env.REACT_APP_BACKEND_URL}${entrepreneur.photo_url}`}
                  alt={entrepreneur.name}
                  className="w-28 h-28 rounded-xl object-cover border-2 border-slate-200 shadow-sm"
                  data-testid="entrepreneur-photo"
                />
              ) : (
                <div className="w-28 h-28 rounded-xl bg-emerald-100 flex items-center justify-center border-2 border-emerald-200">
                  <span className="text-3xl font-bold text-emerald-600">{entrepreneur.name?.charAt(0)?.toUpperCase()}</span>
                </div>
              )}
              <Link
                to={`/entrepreneurs/${entrepreneur.id}`}
                className="mt-2 text-xs text-emerald-600 hover:text-emerald-800 hover:underline flex items-center gap-1 justify-center"
                data-testid="view-entrepreneur-profile"
              >
                View Profile <ExternalLink className="w-3 h-3" />
              </Link>
            </div>
            {/* Details Grid */}
            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-slate-600">Name</p>
                <p className="text-base font-medium text-slate-900">{entrepreneur.name}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Mobile</p>
                <p className="text-base font-medium text-slate-900">{entrepreneur.mobile}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Address</p>
                <p className="text-base font-medium text-slate-900">{entrepreneur.address}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">District / State</p>
                <p className="text-base font-medium text-slate-900">{entrepreneur.district}, {entrepreneur.state}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">SHG/VLF Name</p>
                <p className="text-base font-medium text-slate-900">{entrepreneur.shg_vlf_name}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">SHG Member</p>
                <p className="text-base font-medium text-slate-900">{entrepreneur.shg_member_name} ({entrepreneur.shg_member_relation})</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Enterprise</p>
                <p className="text-base font-medium text-slate-900">{entrepreneur.enterprise?.name} - {entrepreneur.enterprise?.type}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Promoted by CRP-EP</p>
                <p className="text-base font-medium text-emerald-700">{entrepreneur.crp_ep_name || 'Not assigned'}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* EMI Schedule - NRLM SHG Reducing Balance */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-200">
            <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-emerald-700" />
              EMI Schedule
            </h2>
            <p className="text-xs text-slate-500 mt-1">NRLM SHG Reducing Balance Method - Fixed Principal + Interest on Outstanding</p>
          </div>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-emerald-700 text-white sticky top-0">
                <tr>
                  <th className="px-2 py-2 text-center font-semibold">#</th>
                  <th className="px-2 py-2 text-left font-semibold">Due Date</th>
                  <th className="px-2 py-2 text-right font-semibold">Opening Bal</th>
                  <th className="px-2 py-2 text-right font-semibold">Principal</th>
                  <th className="px-2 py-2 text-right font-semibold">Interest</th>
                  <th className="px-2 py-2 text-right font-semibold">EMI</th>
                  <th className="px-2 py-2 text-right font-semibold">Closing Bal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {emiSchedule.map((item) => (
                  <tr key={item.installment} className="hover:bg-slate-50 transition-colors">
                    <td className="px-2 py-2 text-center text-slate-600">{item.installment}</td>
                    <td className="px-2 py-2 text-slate-700">
                      {new Date(item.dueDate).toLocaleDateString('en-IN')}
                    </td>
                    <td className="px-2 py-2 text-right text-slate-600">
                      ₹{(item.openingBalance || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </td>
                    <td className="px-2 py-2 text-right text-purple-700 font-medium">
                      ₹{item.principal.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </td>
                    <td className="px-2 py-2 text-right text-blue-700">
                      ₹{item.interest.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </td>
                    <td className="px-2 py-2 text-right font-semibold text-emerald-700">
                      ₹{item.emi.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </td>
                    <td className="px-2 py-2 text-right text-slate-700">
                      ₹{item.balance.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </td>
                  </tr>
                ))}
                {/* Totals Row */}
                <tr className="bg-emerald-50 font-semibold border-t-2 border-emerald-200">
                  <td colSpan="3" className="px-2 py-2 text-slate-700">Total</td>
                  <td className="px-2 py-2 text-right text-purple-800">
                    ₹{loan?.loan_amount?.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </td>
                  <td className="px-2 py-2 text-right text-blue-800">
                    ₹{emiSchedule.reduce((sum, i) => sum + i.interest, 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </td>
                  <td className="px-2 py-2 text-right text-emerald-800">
                    ₹{emiSchedule.reduce((sum, i) => sum + i.emi, 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </td>
                  <td className="px-2 py-2 text-right">-</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Collection History */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-200">
            <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
              <IndianRupee className="w-5 h-5 text-emerald-700" />
              Collection History
            </h2>
          </div>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            {collections.length === 0 ? (
              <div className="p-6 text-center text-slate-500">No collections recorded yet</div>
            ) : (
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                  <tr>
                    <th className="px-3 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Sl.No</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Date</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Principal</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Interest</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Penalty</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Total</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Receipt</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {collections.map((col, index) => (
                    <tr key={col.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-3 py-3 text-sm text-center text-slate-900">{index + 1}</td>
                      <td className="px-3 py-3 text-sm text-slate-700">
                        {new Date(col.collection_date).toLocaleDateString('en-IN')}
                      </td>
                      <td className="px-3 py-3 text-sm text-right text-slate-700">
                        ₹{(col.principal_amount || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="px-3 py-3 text-sm text-right text-slate-700">
                        ₹{(col.interest_amount || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="px-3 py-3 text-sm text-right text-amber-600">
                        ₹{(col.penalty_amount || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="px-3 py-3 text-sm text-right font-semibold text-emerald-700">
                        ₹{col.amount.toLocaleString('en-IN')}
                      </td>
                      <td className="px-3 py-3 text-sm text-slate-700">{col.receipt_number || '-'}</td>
                    </tr>
                  ))}
                  {/* Totals Row */}
                  <tr className="bg-emerald-50 font-semibold">
                    <td className="px-3 py-3 text-sm text-center text-slate-900"></td>
                    <td className="px-3 py-3 text-sm text-slate-900">TOTAL</td>
                    <td className="px-3 py-3 text-sm text-right text-slate-900">
                      ₹{collections.reduce((sum, c) => sum + (c.principal_amount || 0), 0).toLocaleString('en-IN')}
                    </td>
                    <td className="px-3 py-3 text-sm text-right text-slate-900">
                      ₹{collections.reduce((sum, c) => sum + (c.interest_amount || 0), 0).toLocaleString('en-IN')}
                    </td>
                    <td className="px-3 py-3 text-sm text-right text-amber-700">
                      ₹{collections.reduce((sum, c) => sum + (c.penalty_amount || 0), 0).toLocaleString('en-IN')}
                    </td>
                    <td className="px-3 py-3 text-sm text-right text-emerald-800">
                      ₹{collections.reduce((sum, c) => sum + (c.amount || 0), 0).toLocaleString('en-IN')}
                    </td>
                    <td className="px-3 py-3 text-sm text-slate-700"></td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Loan Info */}
      <div className="mt-6 bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Loan Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-slate-600">Loan Type</p>
            <p className="text-base font-medium text-slate-900">{loan.loan_type}</p>
          </div>
          <div>
            <p className="text-sm text-slate-600">Interest Rate</p>
            <p className="text-base font-medium text-slate-900">{loan.interest_rate}% p.a.</p>
          </div>
          <div>
            <p className="text-sm text-slate-600">Tenure</p>
            <p className="text-base font-medium text-slate-900">{loan.tenure_months} months</p>
          </div>
          <div>
            <p className="text-sm text-slate-600">Disbursement Date</p>
            <p className="text-base font-medium text-slate-900">
              {new Date(loan.disbursement_date).toLocaleDateString('en-IN')}
            </p>
          </div>
          <div>
            <p className="text-sm text-slate-600">Total Amount Payable</p>
            <p className="text-base font-medium text-slate-900">₹{loan.total_amount.toLocaleString('en-IN')}</p>
          </div>
          <div>
            <p className="text-sm text-slate-600">Status</p>
            <span
              className={`inline-block text-xs px-2.5 py-0.5 rounded-full font-medium ${
                loan.status === 'active'
                  ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                  : loan.status === 'closed'
                  ? 'bg-slate-100 text-slate-700 border border-slate-200'
                  : 'bg-red-100 text-red-800 border border-red-200'
              }`}
            >
              {loan.status}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoanDetail;